import { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Input } from '@knack/asterisk-react';

import { type SearchViewCriteriaOperator } from '@/types/schema/views/SearchView';
import { FilterAddressRangeInput } from '@/components/views/view-options/filters-inputs/FilterAddressRangeInput';

function formatAddress(address: string | Record<string, string>): string {
  if (typeof address === 'string') {
    return address;
  }
  if (typeof address !== 'object' || Array.isArray(address)) {
    return '';
  }
  return Object.values(address).join(' ').replace(/\s+/g, ' ').trim();
}

function AddressTextInput({ formValueKey }: { formValueKey: string }) {
  const { getValues, setValue } = useFormContext();

  const inputValue = getValues(formValueKey);

  // We do not support searching records by address objects.
  // We only support searching by an address string.
  useEffect(() => {
    if (typeof inputValue === 'string') return;
    setValue(formValueKey, formatAddress(inputValue));
  }, [inputValue, formValueKey, setValue]);

  return (
    <Controller
      name={formValueKey}
      render={({ field: { value, onChange } }) => {
        const address = formatAddress(value);
        return (
          <Input
            className="h-9"
            value={address}
            onChange={(e) => {
              onChange(e.target.value);
            }}
          />
        );
      }}
    />
  );
}

type AddressInputProps = {
  formValueKey: string;
  searchViewCriteriaOperator: SearchViewCriteriaOperator;
  searchViewCriteriaIndex: number;
};

export function AddressInput({
  searchViewCriteriaOperator: criteriaOperator,
  formValueKey,
  searchViewCriteriaIndex
}: AddressInputProps) {
  if (criteriaOperator === 'near') {
    return <FilterAddressRangeInput criteriaIndex={searchViewCriteriaIndex} />;
  }
  return <AddressTextInput formValueKey={formValueKey} />;
}
